import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PreferenceCenterModal } from './PreferenceCenterModal';
import { OneTrustIcon } from './OneTrustIcon';
import { CommunicationInfoModal } from './CommunicationInfoModal';

export function CommunicationPreferences() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState('');
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userToken, setUserToken] = useState('');
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function fetchUserProfile() {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;

        if (mounted) {
          setUser(user);

          if (user) {
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('token, role')
              .eq('id', user.id)
              .single();

            if (profileError) throw profileError;

            if (mounted && profile) {
              setUserToken(profile.token || '');
              setUserRole(profile.role || null);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchUserProfile();

    return () => {
      mounted = false;
    };
  }, []);

  const preferenceCenters = [
    {
      title: "Single Page Preference Center",
      description: "A streamlined, single-page interface for managing all communication preferences",
      loginUrl: "https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/login/2bb33cf0-335b-4ec3-8dd8-764ee4d8f542",
      tokenUrl: `https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/token/2bb33cf0-335b-4ec3-8dd8-764ee4d8f542/${userToken}`
    },
    {
      title: "Two-Page Profile & Preferences",
      description: "Separate pages for profile management and communication preferences",
      loginUrl: "https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/login/85e24ca5-1c24-4030-8b9b-9f93c39a9d13",
      tokenUrl: `https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/token/85e24ca5-1c24-4030-8b9b-9f93c39a9d13/${userToken}`
    },
    {
      title: "Multi-Brand Preference Center",
      description: "Manage preferences across multiple brands in a single interface",
      loginUrl: "https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/login/e07e53a3-21f7-4a9f-82a1-6c661075dbcc",
      tokenUrl: `https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/token/e07e53a3-21f7-4a9f-82a1-6c661075dbcc/${userToken}`
    },
    {
      title: "Unified Preferences (Cookies + Marketing)",
      description: "Combined interface for cookie and marketing communication preferences",
      loginUrl: "https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/login/284d2d00-45da-4aab-aef7-e1fc599269c8",
      tokenUrl: `https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/token/284d2d00-45da-4aab-aef7-e1fc599269c8/${userToken}`
    },
    {
      title: "Complete Unified Center",
      description: "All-in-one center for cookies, marketing, DSAR, and preference management",
      loginUrl: "https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/login/b4a7c4dc-b742-42db-b8e8-5a648ac5e352",
      tokenUrl: `https://fllr-privacy.my.onetrust.com/ui/#/preferences/multipage/token/b4a7c4dc-b742-42db-b8e8-5a648ac5e352/${userToken}`,
      underMaintenance: true
    }
  ];

  const handlePreferenceCenter = (center: typeof preferenceCenters[0]) => {
    if (center.underMaintenance && userRole !== 'admin') return;
    const url = (user && userToken) ? center.tokenUrl : center.loginUrl;
    setSelectedUrl(url);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5e9bb8]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link
            to="/privacy-center"
            className="inline-flex items-center text-[#5e9bb8] hover:text-[#499da8] transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Privacy Center
          </Link>
        </div>

        {!user && (
          <div className="bg-[#5e9bb8]/5 dark:bg-[#5e9bb8]/10 rounded-xl p-6 mb-8">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-[#5e9bb8] dark:text-[#5e9bb8] mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-[#5e9bb8] dark:text-[#5e9bb8]">
                  Enhanced Experience Available
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  Create an account to unlock your personalized preference center! When you sign up, we automatically generate a secure token in OneTrust that links to your preferences, allowing you to manage all your communication settings in one place.
                </p>
                <div className="mt-4">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium text-[#5e9bb8] hover:text-[#499da8] dark:text-[#5e9bb8] dark:hover:text-[#499da8] transition-colors duration-200"
                  >
                    Sign in or create account
                    <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 mb-12">
          <div className="flex items-center gap-4 mb-6">
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8]">
              Communication Preferences
            </h1>
            <button
              onClick={() => setIsInfoModalOpen(true)}
              className="relative group"
              aria-label="Learn more about OneTrust communication preferences"
            >
              <div className="absolute -inset-2 bg-green-500 rounded-full opacity-20 info-beacon"></div>
              <OneTrustIcon size="lg" />
            </button>
          </div>
          
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            Select a preference center layout to manage your communication preferences:
          </p>

          <div className="grid gap-8">
            {preferenceCenters.map((center, index) => (
              <div 
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-8 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {center.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {center.description}
                </p>
                <button
                  onClick={() => handlePreferenceCenter(center)}
                  className={`
                    inline-flex items-center px-6 py-3 
                    ${center.underMaintenance 
                      ? userRole === 'admin'
                        ? 'bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] cursor-pointer'
                        : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] cursor-pointer'
                    }
                    text-white rounded-xl font-medium transform 
                    ${!center.underMaintenance || userRole === 'admin' ? 'hover:-translate-y-0.5' : ''} 
                    transition-all duration-300
                  `}
                  disabled={center.underMaintenance && userRole !== 'admin'}
                >
                  {center.underMaintenance ? (
                    <>
                      Under Maintenance
                      {userRole === 'admin' && <span className="ml-2">(Admin Only)</span>}
                    </>
                  ) : (
                    'Access Preference Center'
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-600">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Important Note
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                Changes to your communication preferences may take up to 48 hours to take effect across all our systems. 
                You can update your preferences at any time through any of the preference centers above.
              </p>
            </div>
          </div>
        </div>
      </div>

      <PreferenceCenterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        url={selectedUrl}
      />

      <CommunicationInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </div>
  );
}